import {
  interstitial_modal_fallback_url,
  resolveRemoteUrl,
} from 'src/remoteComponentEntries';

declare const __webpack_init_sharing__: (scope: string) => Promise<void>;
declare const __webpack_share_scopes__: Record<string, unknown>;

const SHARE_SCOPE = 'aac-chrome-interstitial-modal';

interface FederationContainer {
  init: (shareScope: unknown) => Promise<void>;
  get: (module: string) => Promise<() => unknown>;
}

const REMOTE_GLOBAL = 'AACInterstitialModal';
const REMOTE_MODULE = './InterstitialModal';

export const getRemoteEntryUrl = (): string => {
  const origin = resolveRemoteUrl(
    window?.__AAC_CHROME_REMOTE_URLS__?.interstitialModal,
    interstitial_modal_fallback_url,
  );
  return `${origin}/modal/remoteEntry.js`;
};

let scriptPromise: Promise<FederationContainer> | null = null;

const loadRemoteEntryScript = (url: string): Promise<FederationContainer> =>
  new Promise((resolve, reject) => {
    // If the container global already exists (e.g. HMR), reuse it.
    const existing = (window as unknown as Record<string, FederationContainer>)[
      REMOTE_GLOBAL
    ];
    if (existing) {
      resolve(existing);
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      const container = (
        window as unknown as Record<string, FederationContainer>
      )[REMOTE_GLOBAL];
      if (container) {
        resolve(container);
      } else {
        reject(
          new Error(
            `AACInterstitialModal container not found on window after loading ${url}`,
          ),
        );
      }
    };
    script.onerror = () =>
      reject(new Error(`Failed to load AACInterstitialModal remote: ${url}`));
    document.head.appendChild(script);
  });

let containerPromise: Promise<FederationContainer> | null = null;

const getContainer = async (): Promise<FederationContainer> => {
  if (containerPromise) return containerPromise;
  containerPromise = (async () => {
    // Ensure the host's shared modules (React singleton) are registered in the
    // share scope before the remote consumes them.
    await __webpack_init_sharing__(SHARE_SCOPE);
    if (!scriptPromise) {
      scriptPromise = loadRemoteEntryScript(getRemoteEntryUrl());
    }
    const container = await scriptPromise;
    await container.init(__webpack_share_scopes__[SHARE_SCOPE]);
    return container;
  })();
  return containerPromise;
};

export const loadInterstitialModalRemote = async (): Promise<{
  default: unknown;
}> => {
  const container = await getContainer();
  const factory = await container.get(REMOTE_MODULE);
  const mod = factory() as Record<string, unknown>;
  return { default: mod.default ?? mod.InterstitialModal ?? mod };
};
